/**
 * Lou Command Bridge — command router.
 *
 * Turns one command off the socket into the Redux actions and service calls
 * that drive the screen. See ui-command-catalogue.md for the command list.
 */

import type { AppDispatch, RootState } from "@/store/store";
import { setPendingSelection } from "@/store/slices/pendingSelection/pendingSelectionSlice";
import { selectAgeFilterCode } from "@/store/slices/userSelection/selectors";
import {
  updateSelection,
  resetSelection,
} from "@/store/slices/userSelection/userSelection";
import type { UserSelectionState } from "@/store/slices/userSelection/userSelection";
import { navigateTo } from "@/utils/navigationService";
import { fetchSegments } from "@/store/slices/segments/segments.thunk";
import { refreshBrandSets } from "@/store/slices/brands/brandSets.thunk";
import { updateBrandColor } from "@/store/slices/brands/brands.thunk";
import { updateMarketColor } from "@/store/slices/markets/market.thunk";
import {
  updateSegmentColor,
  updateSegmentsOrderV2,
} from "@/store/slices/segments/segments.thunk";
import { setBrandsMappingForMarket } from "@/store/slices/brandsOrderMapping/brandsOrderMappingSlice";
import { setMarkets } from "@/store/slices/markets/marketsSlice";
import { notifyViewUpdated } from "@/store/slices/views/viewsSlice";
import { viewDetailEvicted } from "@/store/slices/viewsV2/viewsV2Slice";
import {
  addFileToDownload,
  setShowExportPopover,
} from "@/store/slices/fileDownload/fileDownloadSlice";
import {
  ExportJobStatus,
  type ExportJobStatusType,
} from "@/constant/export/ExportJobStatus";
import { getExportFileName } from "@/utils/export";
import {
  DISTRIBUTION,
  POINT_IN_TIME,
} from "@/constant/constantsThrougoutProject";
import { DateTime } from "luxon";
import { getDefaultRange } from "@/components/projectCommon/DatePeriod/utils";
import userService from "@/endpoints/userService";
import exportService from "@/endpoints/exportService";
import type { FilterSelectionParam, LouCommandMessage, SeriesType } from "./types";
import { isTraced, louLog, louWarn } from "./louLog";
import {
  turnStarted,
  turnStatus,
  turnThinking,
  messageDelta,
  messageComplete,
  turnFailed,
  turnStopped,
  confirmationRequired,
  conversationUpserted,
  conversationRemoved,
  refetchTurn,
} from "../state/conversationActions";
import {
  DEFAULT_COMMAND_VERSION,
  SUPPORTED_MAJOR_VERSIONS,
  parseVersion,
} from "./version";
import {
  BASES, STAT_TESTS, MODES, INTERVALS, GROUPINGS, CHART_TYPES,
  MODULES, SCREENS, SERIES_TYPES, EXPORT_FORMATS,
  oneOf, ids, marketIds, codes, codesOrEmpty, text, num, obj, period, filters,
} from "./validators";

// ─── Internal helpers ─────────────────────────────────────────────────────────

/** Maps a date range onto the period or the pre/post comparison fields. */
function mapTimePeriod(
  tp: Record<string, unknown>,
  prefix: "period" | "post",
): Partial<UserSelectionState> {
  const resolved = period(tp);
  const { preset } = resolved;
  let { start, end } = resolved;
  // Presets carry no dates; resolve them like the date picker does.
  if (preset !== "CUSTOM") {
    const range = getDefaultRange(preset);
    start = DateTime.fromJSDate(range.startDate).toFormat("yyyy-MM-dd");
    end = DateTime.fromJSDate(range.endDate).toFormat("yyyy-MM-dd");
  }
  if (prefix === "period") {
    return {
      periodType: preset,
      ...(start ? { periodStartDate: start } : {}),
      ...(end ? { periodEndDate: end } : {}),
    };
  }
  return {
    postPeriodType: preset,
    ...(start ? { postStartDate: start } : {}),
    ...(end ? { postEndDate: end } : {}),
  };
}

/** Maps a SeriesType to the corresponding hidden-series key in UserSelectionState */
const HIDDEN_KEY_MAP: Record<SeriesType, keyof UserSelectionState> = {
  brand: "hiddenBrands",
  market: "hiddenMarkets",
  segment: "hiddenSegments",
};

// ─── Selection handlers (commands 1–25) ──────────────────────────────────────

/**
 * Normalises an incoming filter list and pulls the age band out of it.
 *
 * Age is a filter to the sender and to the API, but the store still keeps it
 * in its own `ageRange` field, and the export and save-view paths read only
 * that field. A RANGE selection left in the list alone would render correctly
 * and then be missing from a download, so it is applied to both.
 *
 * The sender does not need the age filter's code: the catalog holds exactly
 * one RANGE-kind filter, so it is resolved here and the incoming code is
 * corrected to match.
 */
function applyFilters(
  value: unknown,
  getState: () => RootState,
): { filters: FilterSelectionParam[]; ageRange: [number, number] | null } {
  const incoming = filters(value, "filters") as FilterSelectionParam[];
  const ageCode = selectAgeFilterCode(getState());

  const applied = incoming.map((f) =>
    f.kind === "RANGE" && ageCode ? { ...f, code: ageCode } : f,
  );

  const age = applied.find((f) => f.kind === "RANGE")?.range;
  return {
    filters: applied,
    // Bounds the wrong way round are swapped rather than refused — the intent
    // is not ambiguous. No RANGE in the list means no age filter.
    ageRange: age
      ? [Math.min(age.min, age.max), Math.max(age.min, age.max)]
      : null,
  };
}

/**
 * 1. selection.apply — change several selections at once.
 *
 * A key that fails its own check is dropped and the rest of the command still
 * applies. Rejecting the whole command over one bad field throws away work
 * that was valid, and leaves the screen further from what was asked for than
 * a partial change would have — the sender asked for five things, got one
 * wrong, and the user sees none of the other four.
 *
 * Skips are logged with the field and the reason. The sender is a model with
 * no other way to find out it got something wrong, and a value that is
 * quietly discarded becomes a confident answer about the wrong data.
 *
 * What is checked here is shape, not whether the dashboard supports the
 * value: `pruneInvalidFields` in the selection listener decides that, per
 * screen, and heals what it rejects. This only has to guarantee that what
 * reaches the store is the right type — the same guarantee a dropdown gives
 * for free by only ever offering real options.
 */
function handleApply(
  params: Record<string, unknown>,
  dispatch: AppDispatch,
  getState: () => RootState,
): void {
  const pending: Partial<UserSelectionState> = {};
  const skipped: string[] = [];

  /**
   * Reads one key of the command into `pending`.
   *
   * A key the sender did not send is not a change, so it is passed over in
   * silence; a key it sent badly is passed over loudly.
   */
  const take = (
    field: string,
    read: () => Partial<UserSelectionState>,
  ): void => {
    if (params[field] === undefined) return;
    try {
      Object.assign(pending, read());
    } catch (error) {
      skipped.push(`${field} (${(error as Error).message})`);
    }
  };

  take("module", () => ({ module: oneOf(params.module, MODULES, "module") }));
  take("screen", () => ({
    screenType: oneOf(params.screen, SCREENS, "screen"),
  }));
  take("brands", () => ({ brands: ids(params.brands, "brands") }));
  take("competitors", () => ({
    secondaryBrands: ids(params.competitors, "competitors"),
  }));
  take("markets", () => ({ markets: marketIds(params.markets, "markets") }));
  take("segments", () => ({ segments: codesOrEmpty(params.segments, "segments") }));
  // filters is typed number[] on UserSelectionState but carries FilterSelection[]
  take("filters", () => {
    const applied = applyFilters(params.filters, getState);
    return {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filters: applied.filters as any,
      ageRange: applied.ageRange,
    };
  });
  take("audience_base", () => ({
    base: oneOf(params.audience_base, BASES, "audience_base"),
  }));
  take("stat_test", () => ({
    statTest: oneOf(params.stat_test, STAT_TESTS, "stat_test"),
  }));
  take("analysis_mode", () => ({
    mode: oneOf(
      params.analysis_mode,
      MODES,
      "analysis_mode",
    ) as UserSelectionState["mode"],
  }));
  take("interval", () => ({
    period: oneOf(params.interval, INTERVALS, "interval"),
  }));
  take("chart_type", () => ({
    visualizationType: oneOf(params.chart_type, CHART_TYPES, "chart_type"),
  }));
  take("grouping", () => ({
    groupedBy: oneOf(params.grouping, GROUPINGS, "grouping"),
  }));
  take("trend_metric", () => ({
    trendsMetric: text(params.trend_metric, "trend_metric"),
  }));
  take("calculation_method", () => ({
    scoreType: text(params.calculation_method, "calculation_method"),
  }));
  take("response", () => ({ response: text(params.response, "response") }));
  take("time_period", () =>
    mapTimePeriod(obj(params.time_period, "time_period"), "period"),
  );
  take("comparison_period", () =>
    mapTimePeriod(obj(params.comparison_period, "comparison_period"), "post"),
  );
  take("distribution", () => {
    const dist = obj(params.distribution, "distribution");
    const out: Partial<UserSelectionState> = {
      profileGroup: text(dist.group, "distribution.group"),
      profileMetric: text(dist.metric, "distribution.metric"),
    };
    if (dist.response !== undefined) {
      out.profileResponse = String(dist.response);
    }
    return out;
  });

  if (skipped.length > 0) {
    louWarn(
      `selection.apply: dropped ${skipped.length} field(s), applied the rest`,
      skipped,
    );
  }

  // Nothing usable arrived. Worth distinguishing the two ways that happens:
  // a command with no fields is a sender bug, a command whose every field
  // failed is a sender that knows the vocabulary but not the values.
  if (Object.keys(pending).length === 0) {
    throw new Error(
      skipped.length > 0
        ? `every field failed its check — ${skipped.join("; ")}`
        : "give at least one field to change",
    );
  }

  dispatch(setPendingSelection(pending));
}

/** 2. selection.set_module */
function handleSetModule(
  params: Record<string, unknown>,
  dispatch: AppDispatch,
): void {
  dispatch(setPendingSelection({ module: oneOf(params.value, MODULES, "value") }));
}

/** 3. selection.set_screen */
function handleSetScreen(
  params: Record<string, unknown>,
  dispatch: AppDispatch,
): void {
  dispatch(setPendingSelection({ screenType: oneOf(params.value, SCREENS, "value") }));
}

/** 4. selection.set_brands */
function handleSetBrands(
  params: Record<string, unknown>,
  dispatch: AppDispatch,
): void {
  dispatch(setPendingSelection({ brands: ids(params.ids, "ids") }));
}

/** 5. selection.add_brands — append without duplicates */
function handleAddBrands(
  params: Record<string, unknown>,
  dispatch: AppDispatch,
  getState: () => RootState,
): void {
  const current = getState().userSelection.brands ?? [];
  const incoming = ids(params.ids, "ids");
  const currentSet = new Set(current.map(String));
  const toAdd = incoming.filter((id) => !currentSet.has(String(id)));
  if (toAdd.length > 0) {
    dispatch(setPendingSelection({ brands: [...current, ...toAdd] }));
  }
}

/** 6. selection.remove_brands */
function handleRemoveBrands(
  params: Record<string, unknown>,
  dispatch: AppDispatch,
  getState: () => RootState,
): void {
  const current = getState().userSelection.brands ?? [];
  const toRemove = new Set(ids(params.ids, "ids").map(String));
  const next = current.filter((id) => !toRemove.has(String(id)));
  dispatch(setPendingSelection({ brands: next }));
}

/** 7. selection.set_competitors */
function handleSetCompetitors(
  params: Record<string, unknown>,
  dispatch: AppDispatch,
): void {
  dispatch(setPendingSelection({ secondaryBrands: ids(params.ids, "ids") }));
}

/** 8. selection.set_markets */
function handleSetMarkets(
  params: Record<string, unknown>,
  dispatch: AppDispatch,
): void {
  dispatch(setPendingSelection({ markets: marketIds(params.ids, "ids") }));
}

/** 9. selection.set_segments */
function handleSetSegments(
  params: Record<string, unknown>,
  dispatch: AppDispatch,
): void {
  dispatch(setPendingSelection({ segments: codes(params.codes, "codes") }));
}

/** 10. selection.clear_segments */
function handleClearSegments(dispatch: AppDispatch): void {
  dispatch(setPendingSelection({ segments: [] }));
}

/**
 * 11. selection.set_filters
 *
 * Age arrives here like any other filter — a `RANGE` selection in the list —
 * because that is what it is to the sender and to the API.
 *
 * The store is the odd one out: it still keeps age in its own `ageRange`
 * field, and the export, save-view and other not-yet-migrated paths read only
 * that field. A `RANGE` selection left in the list alone would show correctly
 * on screen and then be missing from a download. So it is applied to both —
 * kept in the list for the v2 endpoints, and mirrored into `ageRange` for
 * everything else.
 *
 * The sender does not have to know the age filter's code. The catalog has
 * exactly one RANGE-kind filter, so the code is resolved here and the
 * incoming one is corrected to match; a placeholder still lands on the right
 * filter.
 */
function handleSetFilters(
  params: Record<string, unknown>,
  dispatch: AppDispatch,
  getState: () => RootState,
): void {
  const applied = applyFilters(params.filters, getState);
  dispatch(
    setPendingSelection({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filters: applied.filters as any,
      ageRange: applied.ageRange,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any),
  );
}

/** 12. selection.clear_filters — clears the age band with the rest. */
function handleClearFilters(dispatch: AppDispatch): void {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch(setPendingSelection({ filters: [] as any, ageRange: null } as any));
}

/** 13. selection.set_audience_base */
function handleSetAudienceBase(
  params: Record<string, unknown>,
  dispatch: AppDispatch,
): void {
  dispatch(setPendingSelection({ base: oneOf(params.value, BASES, "value") }));
}

/** 14. selection.set_stat_test */
function handleSetStatTest(
  params: Record<string, unknown>,
  dispatch: AppDispatch,
): void {
  dispatch(setPendingSelection({ statTest: oneOf(params.value, STAT_TESTS, "value") }));
}

/** 15. selection.set_analysis_mode */
function handleSetAnalysisMode(
  params: Record<string, unknown>,
  dispatch: AppDispatch,
): void {
  dispatch(
    setPendingSelection({
      mode: oneOf(params.value, MODES, "value") as UserSelectionState["mode"],
    }),
  );
}

/** 16. selection.set_time_period */
function handleSetTimePeriod(
  params: Record<string, unknown>,
  dispatch: AppDispatch,
): void {
  dispatch(setPendingSelection(mapTimePeriod(params, "period")));
}

/** 17. selection.set_comparison_period */
function handleSetComparisonPeriod(
  params: Record<string, unknown>,
  dispatch: AppDispatch,
): void {
  dispatch(setPendingSelection(mapTimePeriod(params, "post")));
}

/** 18. selection.set_interval */
function handleSetInterval(
  params: Record<string, unknown>,
  dispatch: AppDispatch,
): void {
  dispatch(setPendingSelection({ period: oneOf(params.value, INTERVALS, "value") }));
}

/** 19. selection.set_chart_type */
function handleSetChartType(
  params: Record<string, unknown>,
  dispatch: AppDispatch,
): void {
  dispatch(setPendingSelection({ visualizationType: oneOf(params.value, CHART_TYPES, "value") }));
}

/** 20. selection.set_grouping */
function handleSetGrouping(
  params: Record<string, unknown>,
  dispatch: AppDispatch,
): void {
  dispatch(setPendingSelection({ groupedBy: oneOf(params.value, GROUPINGS, "value") }));
}

/** 21. selection.set_trend_metric */
function handleSetTrendMetric(
  params: Record<string, unknown>,
  dispatch: AppDispatch,
): void {
  dispatch(setPendingSelection({ trendsMetric: text(params.key, "key") }));
}

/** 22. selection.set_calculation_method */
function handleSetCalculationMethod(
  params: Record<string, unknown>,
  dispatch: AppDispatch,
): void {
  dispatch(setPendingSelection({ scoreType: text(params.value, "value") }));
}

/** 23. selection.set_response */
function handleSetResponse(
  params: Record<string, unknown>,
  dispatch: AppDispatch,
): void {
  dispatch(setPendingSelection({ response: text(params.key, "key") }));
}

/** 24. selection.set_distribution */
function handleSetDistribution(
  params: Record<string, unknown>,
  dispatch: AppDispatch,
): void {
  dispatch(
    setPendingSelection({
      profileGroup: text(params.group, "group"),
      profileMetric: text(params.metric, "metric"),
      ...(params.response !== undefined
        ? { profileResponse: String(params.response) }
        : {}),
    }),
  );
}

/** 25. selection.reset */
function handleReset(dispatch: AppDispatch): void {
  dispatch(resetSelection());
}

// ─── Display handlers (commands 26–34) ───────────────────────────────────────

/** 26. display.hide_series — add ids to the hidden set */
function handleHideSeries(
  params: Record<string, unknown>,
  dispatch: AppDispatch,
  getState: () => RootState,
): void {
  const key = HIDDEN_KEY_MAP[oneOf(params.type, SERIES_TYPES, "type") as SeriesType];
  const incoming = ids(params.ids, "ids");

  const current =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ((getState().userSelection[key] as any) as (number | string)[]) ?? [];
  const currentSet = new Set(current.map(String));
  const toAdd = incoming.filter((id) => !currentSet.has(String(id)));
  if (toAdd.length > 0) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch(updateSelection({ [key]: [...current, ...toAdd] } as any));
  }
}

/** 27. display.show_series — remove ids from the hidden set */
function handleShowSeries(
  params: Record<string, unknown>,
  dispatch: AppDispatch,
  getState: () => RootState,
): void {
  const key = HIDDEN_KEY_MAP[oneOf(params.type, SERIES_TYPES, "type") as SeriesType];

  const current =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ((getState().userSelection[key] as any) as (number | string)[]) ?? [];
  const toRemove = new Set(ids(params.ids, "ids").map(String));
  const next = current.filter((id) => !toRemove.has(String(id)));
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch(updateSelection({ [key]: next } as any));
}

/** 28. display.show_all_series — clear the hidden set for a type */
function handleShowAllSeries(
  params: Record<string, unknown>,
  dispatch: AppDispatch,
): void {
  const key = HIDDEN_KEY_MAP[oneOf(params.type, SERIES_TYPES, "type") as SeriesType];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch(updateSelection({ [key]: [] } as any));
}

/** 29. display.set_brand_order — mirrors useUpdateBrandsOrder. */
function handleSetBrandOrder(
  params: Record<string, unknown>,
  dispatch: AppDispatch,
  getState: () => RootState,
): void {
  const newBrandIdsOrder = ids(params.ids, "ids");
  const state = getState();
  const userId = state.userDetail?.id;
  const marketId = state.userSelection.markets?.[0];
  if (!marketId) return;

  const brands = state.activeGroup.brands;
  const userSelectedBrandsOrder = (
    state.activeGroup.brandsOrderMapping as Record<string, (number | string)[]>
  )[String(marketId)] ?? [];

  const newOrderSet = new Set(newBrandIdsOrder.map(String));
  const selectedSet = new Set(userSelectedBrandsOrder.map(String));

  const remainingBrands = (brands?.ids ?? []).filter(
    (id) => !newOrderSet.has(String(id)),
  );

  const updatedBrandsOrder = [...newBrandIdsOrder, ...remainingBrands].filter(
    (brandId) => selectedSet.has(String(brandId)),
  );

  dispatch(setBrandsMappingForMarket({ marketId, order: updatedBrandsOrder }));

  const payload = updatedBrandsOrder.map((brandId, index) => ({
    entityId: brandId,
    position: index + 1,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    entityType: (brands.entities as any)?.[brandId]?.type,
  }));

  if (userId) {
    userService.brands.updateBrandPosition({ userId, marketId, payload });
  }
}

/** 30. display.set_market_order — mirrors useUpdateMarketsOrder. */
function handleSetMarketOrder(
  params: Record<string, unknown>,
  dispatch: AppDispatch,
  getState: () => RootState,
): void {
  const newMarketIdsOrder = marketIds(params.ids, "ids");
  const state = getState();
  const userId = state.userDetail?.id;
  const markets = state.activeGroup.markets;

  const allMarketIds = (markets?.markets?.ids ?? []) as number[];
  const newOrderSet = new Set(newMarketIdsOrder);
  const remainingMarkets = allMarketIds.filter((id) => !newOrderSet.has(id));
  const updatedMarketOrder = [...newMarketIdsOrder, ...remainingMarkets];

  const updateEntities = updatedMarketOrder.map((marketId, index) => ({
    ...(markets?.markets?.entities?.[marketId] ?? {}),
    position: index + 1,
  }));

  const payload = updatedMarketOrder.map((marketId, index) => ({
    entityId: marketId,
    position: index + 1,
  }));

  dispatch(setMarkets({ markets: updateEntities }));

  if (userId) {
    userService.markets.updateMarketOrder({ userId, payload });
  }
}

/** 31. display.set_segment_order */
function handleSetSegmentOrder(
  params: Record<string, unknown>,
  dispatch: AppDispatch,
): void {
  const orderedCodes = codes(params.codes, "codes");
  dispatch(updateSegmentsOrderV2({ orderedCodes }));
}

/** 32. display.set_brand_color */
function handleSetBrandColor(
  params: Record<string, unknown>,
  dispatch: AppDispatch,
  getState: () => RootState,
): void {
  const id = ids([params.id], "id")[0];
  const color = text(params.color, "color");
  const state = getState();
  const userId = state.userDetail?.id;
  if (!userId) return;

  // Use the explicitly provided market_id, or fall back to the first selected market
  const marketId =
    params.market_id !== undefined
      ? num(params.market_id, "market_id")
      : state.userSelection.markets?.[0];
  if (!marketId) return;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const brandType = (state.activeGroup.brands.entities as any)?.[id]?.type ?? "BRAND";

  dispatch(updateBrandColor({ brandId: id, userId, marketId, color, type: brandType }));
}

/** 33. display.set_market_color */
function handleSetMarketColor(
  params: Record<string, unknown>,
  dispatch: AppDispatch,
  getState: () => RootState,
): void {
  const id = num(params.id, "id");
  const color = text(params.color, "color");
  const state = getState();
  const userId = state.userDetail?.id;
  if (!userId) return;

  dispatch(updateMarketColor({ marketId: id, userId, color }));
}

/** 34. display.set_segment_color */
function handleSetSegmentColor(
  params: Record<string, unknown>,
  dispatch: AppDispatch,
): void {
  const code = text(params.code, "code");
  const color = text(params.color, "color");
  dispatch(updateSegmentColor({ code, color }));
}

// ─── Navigation handlers (commands 35–36) ────────────────────────────────────

/** 35. navigation.open_link — same-origin only, so a link cannot redirect off the app. */
function handleOpenLink(params: Record<string, unknown>): void {
  const url = text(params.url, "url");

  // Relative path — navigate directly
  if (url.startsWith("/")) {
    navigateTo(url);
    return;
  }

  try {
    const parsed = new URL(url);
    if (parsed.origin === window.location.origin) {
      navigateTo(parsed.pathname + parsed.search);
    } else {
      console.warn(
        "[LouCommandBridge] navigation.open_link: external URL rejected",
        url,
      );
    }
  } catch {
    // Not a valid absolute URL — treat as a relative path
    if (!url.startsWith("http")) {
      navigateTo(url);
    } else {
      console.warn(
        "[LouCommandBridge] navigation.open_link: invalid URL",
        url,
      );
    }
  }
}

/** 36. navigation.open_view */
function handleOpenView(
  params: Record<string, unknown>,
  dispatch: AppDispatch,
): void {
  dispatch(setPendingSelection({ viewId: text(params.view_id, "view_id") }));
}

// ─── Export handlers (commands 37–38) ────────────────────────────────────────

const EXPORT_SERVICE: Record<string, string> = {
  default: "export",
  distribution: "distribution",
  exclusive_questions: "exclusiveQuestion",
};

/**
 * 38. export.start
 *
 * The payload arrives already built. The endpoint family comes from the
 * payload where it says so, since it describes what is being exported rather
 * than what the user is looking at; Exclusive Questions is only knowable from
 * the current module.
 */
async function handleExportStart(
  params: Record<string, unknown>,
  dispatch: AppDispatch,
  getState: () => RootState,
): Promise<void> {
  const format = oneOf(String(params.format).toLowerCase(), EXPORT_FORMATS, "format");
  const payload = obj(params.payload, "payload");
  const { userSelection } = getState();

  let variant = "default";
  if (userSelection?.module?.toLowerCase() === "exclusivequestions") {
    variant = "exclusive_questions";
  } else {
    const viz =
      typeof payload.visualizationType === "string"
        ? payload.visualizationType
        : userSelection?.visualizationType;
    if (viz === DISTRIBUTION) variant = "distribution";
  }

  const sentName = payload.fileName;
  const fileName =
    typeof sentName === "string" && sentName
      ? sentName
      : getExportFileName(
          window.location.pathname,
          userSelection?.mode || POINT_IN_TIME,
          format,
        );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const service = (exportService as any)[EXPORT_SERVICE[variant]];
  const call = service?.[format];
  if (!call) throw new Error(`no export service for ${variant}/${format}`);

  const jobId = await call({ ...payload, fileName });

  // The service swallows its own errors and returns [] rather than throwing.
  if (typeof jobId !== "string" || !jobId) {
    throw new Error("the export service did not return a job");
  }

  // Without this the job is never tracked and the file never arrives.
  dispatch(
    addFileToDownload({
      fileName,
      jobId,
      status: ExportJobStatus.INPROGRESS as ExportJobStatusType,
      errorMessage: null,
      url: "",
      date: DateTime.now().toFormat("MMM dd, yyyy"),
    }),
  );

  dispatch(setShowExportPopover(true));
}

/**
 * 39. export.track
 *
 * The backend started the export itself and is handing over the job id.
 *
 * Nothing is requested here. The tray is keyed by job id, and progress,
 * delivery and failure arrive on the platform's own export channel — the same
 * one that carries a job the user started from the export panel. This command
 * only says that a job exists and what to call it in the list.
 *
 * That the events come in on a different connection from the one this command
 * arrived on is deliberate, not an oversight. Lou's channel carries the
 * conversation between the agent and this panel; export progress is the
 * platform's own traffic and belongs to the platform's socket, whether or not
 * Lou is open.
 */
function handleExportTrack(
  params: Record<string, unknown>,
  dispatch: AppDispatch,
): void {
  const jobId = text(params.job_id, "job_id");
  // Shown before the file exists, so it has to arrive with the command —
  // there is nothing to derive it from on this side. The backend chose what
  // to export; only it knows what the result should be called.
  const fileName = text(params.file_name, "file_name");

  dispatch(
    addFileToDownload({
      fileName,
      jobId,
      status: ExportJobStatus.INPROGRESS as ExportJobStatusType,
      errorMessage: null,
      url: "",
      date: DateTime.now().toFormat("MMM dd, yyyy"),
    }),
  );

  dispatch(setShowExportPopover(true));
}

// ─── Views handlers (commands 39–40) ─────────────────────────────────────────

/** 38. views.reload_current — re-commits the open view after its definition changed. */
function handleViewsReloadCurrent(
  dispatch: AppDispatch,
  getState: () => RootState,
): void {
  const { viewId } = getState().userSelection;
  if (!viewId) return;
  dispatch(viewDetailEvicted(String(viewId)));
  dispatch(setPendingSelection({ viewId }));
}

/** 39. views.refresh — tells the views list to re-fetch. */
function handleViewsRefresh(
  dispatch: AppDispatch,
  getState: () => RootState,
): void {
  const module = getState().userSelection.module ?? "";
  dispatch(notifyViewUpdated({ module }));
}

// ─── Refresh handlers (commands 41–45) ───────────────────────────────────────

/** 40. segments.refresh */
function handleSegmentsRefresh(dispatch: AppDispatch): void {
  dispatch(fetchSegments());
}

/** 41. brand_sets.refresh */
function handleBrandSetsRefresh(dispatch: AppDispatch): void {
  dispatch(refreshBrandSets());
}

/** 42. reports.refresh */
function handleReportsRefresh(dispatch: AppDispatch): void {
  dispatch(notifyViewUpdated({ module: "reports" }));
}

/** 43. brands.refresh — no brands-only endpoint exists, so reload the workspace. */
function handleBrandsRefresh(): void {
  window.location.reload();
}

/** 44. markets.refresh — no markets-only endpoint exists, so reload the workspace. */
function handleMarketsRefresh(): void {
  window.location.reload();
}


// ─── Conversation handlers (commands 46–60) ──────────────────────────────────

/** Highest `seq` seen per turn. Cleared as each turn ends. */
const lastSeqByTurn = new Map<string, number>();

/**
 * Whether a message should be acted on. A repeat is dropped; a gap means
 * something was missed while disconnected, so the turn is refetched whole.
 */
function accept(
  turnId: string | undefined,
  seq: number | undefined,
  conversationId: string | undefined,
  dispatch: AppDispatch,
): boolean {
  if (!turnId || typeof seq !== "number") return true;
  const seen = lastSeqByTurn.get(turnId) ?? 0;
  if (seq <= seen) {
    louWarn(`dropped: seq ${seq} already seen (last ${seen})`, turnId);
    return false;
  }
  if (seq > seen + 1 && conversationId) {
    console.warn(
      `[LouCommandBridge] Gap in turn ${turnId}: expected ${seen + 1}, got ${seq}. Refetching.`,
    );
    void dispatch(refetchTurn({ conversationId, turnId }) as never);
  }
  lastSeqByTurn.set(turnId, seq);
  return true;
}

function endOfTurn(turnId: string | undefined): void {
  if (turnId) lastSeqByTurn.delete(turnId);
}

// ─── Main router ─────────────────────────────────────────────────────────────

/** Routes one command to its handler. Dispatch it: `dispatch(handleLouCommand(message))`. */
export const handleLouCommand =
  (message: LouCommandMessage) =>
  (dispatch: AppDispatch, getState: () => RootState): void => {
    // The socket may hand us the command directly or still inside its envelope.
    const envelope = (message?.payload ?? message) as LouCommandMessage;
    const { command, params = {}, metadata } = envelope ?? {};

    if (typeof command !== "string" || !command) {
      console.warn("[LouCommandBridge] Message has no command — ignored:", message);
      return;
    }

    // An older sender may still put this at the top level.
    const trace_id = metadata?.trace_id ?? envelope?.trace_id;

    // Which conversation, which exchange, and where in it. Present on every
    // conversation command and on screen commands emitted during a turn.
    const conversation_id = metadata?.conversation_id;
    const turn_id = metadata?.turn_id;
    const seq = metadata?.seq;

    // A major this build does not know is left alone rather than guessed at.
    // A higher minor is fine: it only adds things this build can ignore.
    const raw = metadata?.version ?? DEFAULT_COMMAND_VERSION;
    const version = parseVersion(raw);
    if (!version || !SUPPORTED_MAJOR_VERSIONS.includes(version.major)) {
      console.warn(
        `[LouCommandBridge] Command version "${raw}" is not supported by this build ` +
          `(supports ${SUPPORTED_MAJOR_VERSIONS.map((m) => `${m}.x`).join(", ")}) — ` +
          `"${command}" ignored.`,
      );
      return;
    }

    if (isTraced(command)) {
      louLog(`route ${command}`, { seq, turn_id, conversation_id });
    }

    // Ordering and duplicate suppression apply to everything carrying a
    // turn - a screen command emitted mid-reply is part of that turn too.
    if (!accept(turn_id, seq, conversation_id, dispatch)) return;

    try {
      switch (command) {
        // ── conversation ───────────────────────────────────────────────────
        case "conversation.turn_started":
          dispatch(
            turnStarted({
              turnId: turn_id as string,
              userText: params.user_text as string,
              turnSeq: params.turn_seq as number,
            }),
          );
          break;
        case "conversation.user_message":
          // Sent by someone else's page. `turnStarted` is idempotent, so the
          // page that sent it recognises its own rather than drawing twice.
          dispatch(
            turnStarted({
              turnId: turn_id as string,
              userText: params.user_text as string,
              turnSeq: params.turn_seq as number,
            }),
          );
          break;
        case "conversation.status":
          dispatch(
            turnStatus({
              phase: params.phase as string,
              node: params.node as string | undefined,
            }),
          );
          break;
        case "conversation.thinking":
          dispatch(
            turnThinking({
              turnId: turn_id as string,
              text: params.text as string,
            }),
          );
          break;
        case "conversation.tool_started":
          dispatch(
            turnStatus({
              phase: (params.label as string) || (params.tool_name as string),
              node: params.tool_name as string,
            }),
          );
          break;
        case "conversation.tool_finished":
          // Nothing to show. The next status or the reply itself says what
          // happened next, and announcing every completed fetch would flicker.
          break;
        case "conversation.message_delta":
          dispatch(
            messageDelta({
              turnId: turn_id as string,
              text: params.text as string,
            }),
          );
          break;
        case "conversation.message_complete":
          dispatch(
            messageComplete({
              turnId: turn_id as string,
              text: params.text as string,
              mode: params.mode as string | undefined,
              traceId: (params.trace_id as string) ?? trace_id,
              suggestions: params.suggestions as string[] | undefined,
            }),
          );
          endOfTurn(turn_id);
          break;
        case "conversation.turn_failed":
          dispatch(
            turnFailed({
              turnId: turn_id as string,
              errorCode: params.error_code as string,
              message: params.message as string,
            }),
          );
          endOfTurn(turn_id);
          break;
        case "conversation.turn_stopped":
          // Arrives whichever device pressed stop, so the tab that did not
          // press it stops showing a reply that is no longer coming.
          dispatch(
            turnStopped({
              turnId: turn_id as string,
              text: params.text as string | undefined,
            }),
          );
          endOfTurn(turn_id);
          break;
        case "conversation.confirmation_required":
          dispatch(
            confirmationRequired({
              turnId: turn_id as string,
              estimate: params.estimate as Record<string, unknown>,
              message: params.message as string | undefined,
            }),
          );
          endOfTurn(turn_id);
          break;
        case "conversation.title_set":
          if (conversation_id) {
            dispatch(
              conversationUpserted({
                id: conversation_id,
                title: params.title as string,
              }),
            );
          }
          break;
        case "conversation.created":
          dispatch(
            conversationUpserted({
              id: params.conversation_id as string,
              title: (params.title as string) ?? null,
              created_at: params.created_at as string,
              updated_at: params.created_at as string,
              message_count: 0,
            }),
          );
          break;
        case "conversation.updated":
          dispatch(
            conversationUpserted({
              id: params.conversation_id as string,
              title: params.title as string | null,
              preview: params.preview as string | null,
              message_count: params.message_count as number,
              updated_at: params.updated_at as string,
            }),
          );
          break;
        case "conversation.deleted":
          dispatch(conversationRemoved(params.conversation_id as string));
          break;

        // ── selection ──────────────────────────────────────────────────────
        case "selection.apply":
          handleApply(params, dispatch, getState);
          break;
        case "selection.set_module":
          handleSetModule(params, dispatch);
          break;
        case "selection.set_screen":
          handleSetScreen(params, dispatch);
          break;
        case "selection.set_brands":
          handleSetBrands(params, dispatch);
          break;
        case "selection.add_brands":
          handleAddBrands(params, dispatch, getState);
          break;
        case "selection.remove_brands":
          handleRemoveBrands(params, dispatch, getState);
          break;
        case "selection.set_competitors":
          handleSetCompetitors(params, dispatch);
          break;
        case "selection.set_markets":
          handleSetMarkets(params, dispatch);
          break;
        case "selection.set_segments":
          handleSetSegments(params, dispatch);
          break;
        case "selection.clear_segments":
          handleClearSegments(dispatch);
          break;
        case "selection.set_filters":
          handleSetFilters(params, dispatch, getState);
          break;
        case "selection.clear_filters":
          handleClearFilters(dispatch);
          break;
        case "selection.set_audience_base":
          handleSetAudienceBase(params, dispatch);
          break;
        case "selection.set_stat_test":
          handleSetStatTest(params, dispatch);
          break;
        case "selection.set_analysis_mode":
          handleSetAnalysisMode(params, dispatch);
          break;
        case "selection.set_time_period":
          handleSetTimePeriod(params, dispatch);
          break;
        case "selection.set_comparison_period":
          handleSetComparisonPeriod(params, dispatch);
          break;
        case "selection.set_interval":
          handleSetInterval(params, dispatch);
          break;
        case "selection.set_chart_type":
          handleSetChartType(params, dispatch);
          break;
        case "selection.set_grouping":
          handleSetGrouping(params, dispatch);
          break;
        case "selection.set_trend_metric":
          handleSetTrendMetric(params, dispatch);
          break;
        case "selection.set_calculation_method":
          handleSetCalculationMethod(params, dispatch);
          break;
        case "selection.set_response":
          handleSetResponse(params, dispatch);
          break;
        case "selection.set_distribution":
          handleSetDistribution(params, dispatch);
          break;
        case "selection.reset":
          handleReset(dispatch);
          break;

        // ── display ───────────────────────────────────────────────────────
        case "display.hide_series":
          handleHideSeries(params, dispatch, getState);
          break;
        case "display.show_series":
          handleShowSeries(params, dispatch, getState);
          break;
        case "display.show_all_series":
          handleShowAllSeries(params, dispatch);
          break;
        case "display.set_brand_order":
          handleSetBrandOrder(params, dispatch, getState);
          break;
        case "display.set_market_order":
          handleSetMarketOrder(params, dispatch, getState);
          break;
        case "display.set_segment_order":
          handleSetSegmentOrder(params, dispatch);
          break;
        case "display.set_brand_color":
          handleSetBrandColor(params, dispatch, getState);
          break;
        case "display.set_market_color":
          handleSetMarketColor(params, dispatch, getState);
          break;
        case "display.set_segment_color":
          handleSetSegmentColor(params, dispatch);
          break;

        // ── navigation ────────────────────────────────────────────────────
        case "navigation.open_link":
          handleOpenLink(params);
          break;
        case "navigation.open_view":
          handleOpenView(params, dispatch);
          break;

        // ── export ────────────────────────────────────────────────────────
        case "export.start":
          void handleExportStart(params, dispatch, getState).catch((err) =>
            console.error('[LouCommandBridge] export.start failed:', err),
          );
          break;
        case "export.track":
          handleExportTrack(params, dispatch);
          break;

        // ── views ─────────────────────────────────────────────────────────
        case "views.reload_current":
          handleViewsReloadCurrent(dispatch, getState);
          break;
        case "views.refresh":
          handleViewsRefresh(dispatch, getState);
          break;

        // ── refresh ───────────────────────────────────────────────────────
        case "segments.refresh":
          handleSegmentsRefresh(dispatch);
          break;
        case "brand_sets.refresh":
          handleBrandSetsRefresh(dispatch);
          break;
        case "reports.refresh":
          handleReportsRefresh(dispatch);
          break;
        case "brands.refresh":
          handleBrandsRefresh();
          break;
        case "markets.refresh":
          handleMarketsRefresh();
          break;

        default:
          louWarn(`unknown command ignored: ${command}`);
      }
    } catch (err) {
      console.error(
        `[LouCommandBridge] Error handling command "${command}":`,
        err,
      );
    }
  };
